import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/hooks/use-toast';
import { refreshToken } from '@/lib/api';
import { TaskEvents, NotificationEvents, QuoteEvents } from 'shared';
import { logger } from '@/lib/logger';

const SOCKET_PATH = '/client/api/socket';

// ── Module-level singleton — shared by all callers ─────────────────
// Prevents duplicate connections when multiple components call useSocket()
// (e.g. SocketInitializer in App.tsx + useProjectRoom in project-detail)
let _socket: Socket | null = null;
let _callerCount = 0;

// Auth-related connection errors — the access token expired or was revoked
// mid-session. When the socket drops and reconnects with a stale token this is
// the failure mode that previously left realtime dead until a page reload.
function isAuthError(message: string): boolean {
  return /invalid or expired token|authentication required|not authorized/i.test(
    message,
  );
}

/**
 * Hook that maintains a Socket.IO connection while the user is authenticated.
 * Listens for real-time task events and invalidates the React Query cache
 * so the Kanban board and task lists update automatically.
 *
 * Uses only the 'polling' transport so it works with Phusion Passenger
 * (cPanel), which cannot upgrade HTTP → WebSocket connections.
 *
 * The connection is self-healing:
 * - reconnects forever (capped backoff) instead of giving up after 10 tries
 * - rotates the access token before reconnecting if auth fails (the API-layer
 *   refresh is deduplicated; a failed refresh means the session is dead and
 *   the auth store force-logs-out)
 * - reconnects when the browser tab becomes visible again (background tabs
 *   have throttled timers that starve the polling heartbeat)
 * - only surfaces a toast after several consecutive failures, so transient
 *   blips don't alarm the user
 *
 * Safe to call from multiple components — only the first call creates the
 * connection; subsequent calls share the same singleton. The connection is
 * torn down only when the last caller unmounts.
 */
export function useSocket() {
  const { token, isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    _callerCount++;

    if (!isAuthenticated || !token) {
      // Disconnect if user logs out
      if (_socket) {
        _socket.disconnect();
        _socket = null;
      }
      return () => { _callerCount--; };
    }

    // Only the first caller creates the connection
    if (!_socket) {
      const socket = io(undefined, {
        path: SOCKET_PATH,
        auth: { token },
        transports: ['polling'],
        autoConnect: true,
        reconnection: true,
        // Never give up permanently — with capped backoff this is at most one
        // attempt per 30s, and the auth-refresh path below heals stale tokens.
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2_000,
        reconnectionDelayMax: 30_000,
      });

      _socket = socket;

      // ── Task Event Handlers ──────────────────────────────────────────

      socket.on(TaskEvents.Created, (payload) => {
        logger.debug('[Socket] task:created', payload);
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      });

      socket.on(TaskEvents.Updated, (payload) => {
        logger.debug('[Socket] task:updated', payload);
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['task', payload.taskId] });
      });

      socket.on(TaskEvents.StatusChanged, (payload) => {
        logger.debug('[Socket] task:statusChanged', payload);
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      });

      socket.on(TaskEvents.Moved, (payload) => {
        logger.debug('[Socket] task:moved', payload);
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      });

      socket.on(TaskEvents.Deleted, (payload) => {
        logger.debug('[Socket] task:deleted', payload);
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.removeQueries({ queryKey: ['task', payload.taskId] });
      });

      // ── Quote Event Handlers ────────────────────────────────────────

      socket.on(QuoteEvents.Created, (payload) => {
        logger.debug('[Socket] quote:created', payload);
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      });

      socket.on(QuoteEvents.StatusChanged, (payload) => {
        logger.debug('[Socket] quote:statusChanged', payload);
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      });

      // ── Notification Event Handlers ──────────────────────────────────

      socket.on(NotificationEvents.New, (payload) => {
        logger.debug('[Socket] notification:new', payload);
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        toast({
          title: payload.title,
          description: payload.message,
        });
      });

      // ── Connection Lifecycle ────────────────────────────────────────

      // Consecutive failed reconnect attempts since the last successful
      // connect — used to only alarm the user after sustained failure.
      let _consecutiveErrors = 0;
      let _errorToastShown = false;

      socket.on('connect', () => {
        logger.debug('[Socket] Connected');
        // Recovery resets the failure streak so a later outage surfaces fresh.
        _consecutiveErrors = 0;
        _errorToastShown = false;
      });

      socket.on('disconnect', (reason) => {
        logger.debug('[Socket] Disconnected:', reason);
      });

      socket.on('connect_error', (err) => {
        logger.warn('[Socket] Connection error:', err.message);
        _consecutiveErrors++;

        // If the access token expired/revoked mid-session, refresh it so the
        // next reconnection attempt authenticates. refreshToken() is
        // deduplicated; a null result means the refresh token itself is dead —
        // the API layer emits the session-expired event and the auth store
        // force-logs-out, so there is nothing more to do here.
        if (isAuthError(err.message)) {
          refreshToken().then((newToken) => {
            if (newToken && socket.auth) {
              socket.auth = { token: newToken };
              if (!socket.connected) {
                socket.connect();
              }
            }
            // Deliberately NOT syncing the zustand store here: refreshToken()
            // already persisted the new token to localStorage (which the API
            // layer reads via getToken()), and updating the store would re-run
            // this effect and tear down the very connection we are healing.
          });
        }

        // Only warn the user once, and only after the reconnect has failed a
        // few times — transient blips self-heal and shouldn't alarm anyone.
        if (!import.meta.env.DEV && !_errorToastShown && _consecutiveErrors >= 3) {
          _errorToastShown = true;
          toast({
            title: 'Live updates unavailable',
            description:
              'Real-time updates could not be re-established. The page will keep working and will keep retrying automatically.',
            duration: 8000,
          });
        }
      });
    }

    // Keep the connection's auth payload fresh — the access token rotates on
    // every refresh, and socket.io re-sends `socket.auth` on reconnection.
    if (_socket && token) {
      _socket.auth = { token };
    }

    // Reconnect when the user returns to the tab. Background tabs get their
    // timers throttled by the browser, which can starve the polling heartbeat
    // and silently kill the connection while the user is away.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && _socket && !_socket.connected) {
        logger.debug('[Socket] Tab visible again — reconnecting');
        _socket.connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      _callerCount--;
      if (_callerCount <= 0 && _socket) {
        logger.debug('[Socket] Last caller cleanup — disconnecting');
        _socket.disconnect();
        _socket = null;
      }
    };
  }, [isAuthenticated, token, queryClient]);
}

/**
 * Component that connects to Socket.IO when the user is authenticated.
 * Place this inside the protected area of your app (e.g. in App.tsx).
 */
export function SocketInitializer() {
  useSocket();
  return null;
}

/**
 * Join a Socket.IO room for real-time updates on a specific project.
 */
export function joinProjectRoom(socket: Socket | null, projectId: string) {
  if (socket?.connected) {
    socket.emit('room:join', `project:${projectId}`);
  }
}

/**
 * Leave a Socket.IO room when navigating away from a project page.
 */
export function leaveProjectRoom(socket: Socket | null, projectId: string) {
  if (socket?.connected) {
    socket.emit('room:leave', `project:${projectId}`);
  }
}

/**
 * Hook that joins a project's Socket.IO room when the user views the project
 * detail page, and leaves when they navigate away.
 *
 * Uses the module-level singleton socket — does NOT create a separate connection.
 *
 * @param projectId - The project ID from the URL params. Pass undefined when not on a project page.
 */
export function useProjectRoom(projectId: string | undefined) {
  useEffect(() => {
    if (!projectId) return;

    const socket = _socket;

    // Join immediately if already connected
    if (socket?.connected) {
      joinProjectRoom(socket, projectId);
    }

    // Listen for connect event to join when the socket comes online
    const handleConnect = () => {
      joinProjectRoom(_socket, projectId);
    };
    socket?.on('connect', handleConnect);

    return () => {
      leaveProjectRoom(_socket, projectId);
      socket?.off('connect', handleConnect);
    };
  }, [projectId]);
}
